import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut, RefreshCw, Loader2, FileSpreadsheet, Upload,
    Download, Copy, Pencil, Trash2, Search, ClipboardList, Inbox,
} from "lucide-react";
import { adminAxios, getAdminToken, clearAdminToken, formatApiError } from "@/lib/adminApi";
import { LOGO_URL } from "@/lib/brand";
import { useNoIndex } from "@/lib/useNoIndex";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel,
    AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ALLOWED_EXT = ["xlsx", "xls", "csv"];
const MAX_MB = 25;

function fmtSize(n) {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
function fmtDate(iso) {
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
    } catch { return iso || "—"; }
}
function extOf(name) {
    if (!name || !name.includes(".")) return "";
    return name.split(".").pop().toLowerCase();
}

function AdminTopNav({ current }) {
    const navigate = useNavigate();
    const logout = () => { clearAdminToken(); navigate("/admin/login", { replace: true }); };
    const tabs = [
        { key: "questionnaire", label: "Questionnaires", to: "/admin/questionnaire" },
        { key: "spreadsheets", label: "Spreadsheets", to: "/admin/spreadsheets" },
    ];
    return (
        <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/[0.05]">
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                    <img src={LOGO_URL} alt="RecoveryDesk" className="h-10 w-10 object-cover rounded-sm ring-1 ring-[#D4AF37]/30" />
                    <div className="min-w-0">
                        <div className="font-display text-lg tracking-wide text-silver-gold leading-none truncate">RecoveryDesk</div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-1">Admin</div>
                    </div>
                </div>
                <nav className="hidden md:flex items-center gap-8">
                    {tabs.map((t) => (
                        <button
                            key={t.key}
                            data-testid={`admin-tab-${t.key}`}
                            onClick={() => navigate(t.to)}
                            className={`text-[11px] uppercase tracking-[0.22em] transition-colors ${
                                current === t.key ? "text-[#D4AF37]" : "text-[#C4C4C4] hover:text-[#D4AF37]"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>
                <button
                    data-testid="admin-signout-btn"
                    onClick={logout}
                    className="inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/[0.08] px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-all"
                >
                    <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="hidden sm:inline">Sign Out</span>
                </button>
            </div>
            <div className="md:hidden border-t border-white/[0.05] bg-black/95">
                <nav className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-6 overflow-x-auto">
                    {tabs.map((t) => (
                        <button
                            key={t.key}
                            data-testid={`admin-tab-mobile-${t.key}`}
                            onClick={() => navigate(t.to)}
                            className={`whitespace-nowrap text-[11px] uppercase tracking-[0.22em] py-2 ${
                                current === t.key ? "text-[#D4AF37] border-b border-[#D4AF37]" : "text-[#C4C4C4]"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>
            </div>
        </header>
    );
}

export default function AdminSpreadsheets() {
    useNoIndex();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("last_modified_at");
    const [order, setOrder] = useState("desc");
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [renameTarget, setRenameTarget] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const fileInputRef = useRef(null);

    // Auth guard
    useEffect(() => {
        if (!getAdminToken()) navigate("/admin/login", { replace: true });
    }, [navigate]);

    // Debounced search
    const [debouncedQ, setDebouncedQ] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
        return () => clearTimeout(t);
    }, [q]);

    // Load list
    useEffect(() => {
        if (!getAdminToken()) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await adminAxios.get("/admin/spreadsheets", {
                    params: { q: debouncedQ || undefined, sort, order, limit: 200 },
                });
                if (!cancelled) {
                    setItems(data.items || []);
                    setTotal(data.total || 0);
                }
            } catch (err) {
                if (!cancelled && err?.response?.status !== 401) {
                    toast.error(formatApiError(err?.response?.data?.detail));
                }
            } finally {
                if (!cancelled) { setLoading(false); setRefreshing(false); }
            }
        })();
        return () => { cancelled = true; };
    }, [debouncedQ, sort, order, reloadKey]);

    const doUpload = useCallback(async (file) => {
        if (!file) return;
        const ext = extOf(file.name);
        if (!ALLOWED_EXT.includes(ext)) {
            toast.error(`Only ${ALLOWED_EXT.join(", ")} files are supported.`);
            return;
        }
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`File exceeds ${MAX_MB} MB limit.`);
            return;
        }
        setUploading(true);
        const form = new FormData();
        form.append("file", file);
        try {
            await adminAxios.post("/admin/spreadsheets/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Uploaded ${file.name}`);
            setReloadKey((k) => k + 1);
        } catch (err) {
            if (err?.response?.status !== 401) {
                toast.error(formatApiError(err?.response?.data?.detail));
            }
        } finally {
            setUploading(false);
        }
    }, []);

    const onPickFile = () => fileInputRef.current?.click();
    const onFileInput = (e) => {
        const f = e.target.files?.[0];
        if (f) doUpload(f);
        e.target.value = "";
    };
    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) doUpload(f);
    };

    const downloadOne = async (row) => {
        try {
            const { data, headers } = await adminAxios.get(`/admin/spreadsheets/${row.id}/download`, { responseType: "blob" });
            const m = /filename="?([^"]+)"?/.exec(headers["content-disposition"] || "");
            const name = m ? m[1] : row.name;
            const url = URL.createObjectURL(new Blob([data]));
            const a = document.createElement("a");
            a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            if (err?.response?.status !== 401) toast.error("Download failed.");
        }
    };

    const duplicate = async (row) => {
        try {
            await adminAxios.post(`/admin/spreadsheets/${row.id}/duplicate`);
            toast.success("Duplicated.");
            setReloadKey((k) => k + 1);
        } catch (err) {
            if (err?.response?.status !== 401) toast.error(formatApiError(err?.response?.data?.detail));
        }
    };

    const doDelete = async (id) => {
        try {
            await adminAxios.delete(`/admin/spreadsheets/${id}`);
            setItems((p) => p.filter((r) => r.id !== id));
            setTotal((t) => Math.max(0, t - 1));
            toast.success("Deleted.");
        } catch (err) {
            if (err?.response?.status !== 401) toast.error(formatApiError(err?.response?.data?.detail));
        }
    };

    const openRename = (row) => {
        setRenameTarget(row);
        setRenameValue(row.name);
    };

    const submitRename = async () => {
        if (!renameTarget) return;
        const trimmed = renameValue.trim();
        if (!trimmed || trimmed === renameTarget.name) { setRenameTarget(null); return; }
        try {
            await adminAxios.patch(`/admin/spreadsheets/${renameTarget.id}/rename`, { name: trimmed });
            toast.success("Renamed.");
            setRenameTarget(null);
            setReloadKey((k) => k + 1);
        } catch (err) {
            if (err?.response?.status !== 401) toast.error(formatApiError(err?.response?.data?.detail));
        }
    };

    const sortOptions = useMemo(() => ([
        { value: "last_modified_at", label: "Last Modified" },
        { value: "uploaded_at", label: "Upload Date" },
        { value: "name", label: "Name" },
        { value: "size", label: "Size" },
    ]), []);

    if (!getAdminToken()) return null;

    return (
        <main data-testid="admin-spreadsheets-page" className="relative min-h-screen text-white font-body">
            <AdminTopNav current="spreadsheets" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="gold-rule-solid w-10" />
                            <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">Library</span>
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl text-white tracking-tight leading-tight">Spreadsheets</h1>
                    </div>
                    <div className="font-display text-3xl md:text-4xl text-gold-metallic">
                        {total}
                        <span className="text-sm text-[#888] font-body uppercase tracking-[0.25em] ml-3">total</span>
                    </div>
                </div>

                {/* Upload dropzone */}
                <div
                    data-testid="upload-dropzone"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`relative border-2 border-dashed transition-colors mb-8 ${
                        dragOver ? "border-[#D4AF37] bg-[#D4AF37]/[0.05]" : "border-[#D4AF37]/25 bg-black/40"
                    } p-8 md:p-10 text-center`}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={onFileInput}
                        className="hidden"
                        data-testid="upload-input"
                    />
                    <Upload className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" strokeWidth={1.25} />
                    <div className="font-display text-xl text-white mb-2">
                        {uploading ? "Uploading…" : "Drop a spreadsheet or click to browse"}
                    </div>
                    <p className="text-xs text-[#888] mb-4">
                        Accepts XLSX, XLS, CSV · max {MAX_MB} MB
                    </p>
                    <button
                        data-testid="upload-browse-btn"
                        disabled={uploading}
                        onClick={onPickFile}
                        className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#E5C158] disabled:opacity-60"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Choose File
                    </button>
                </div>

                {/* Search + sort */}
                <div className="flex flex-wrap items-center gap-3 mb-6">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" strokeWidth={1.5} />
                        <input
                            data-testid="sheets-search"
                            type="text"
                            placeholder="Search by name…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-[#666] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
                        />
                    </div>
                    <select
                        data-testid="sheets-sort"
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="bg-[#0A0A0A] border border-white/10 px-3 py-2.5 text-sm text-white focus:border-[#D4AF37] outline-none"
                    >
                        {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <select
                        data-testid="sheets-order"
                        value={order}
                        onChange={(e) => setOrder(e.target.value)}
                        className="bg-[#0A0A0A] border border-white/10 px-3 py-2.5 text-sm text-white focus:border-[#D4AF37] outline-none"
                    >
                        <option value="desc">Descending</option>
                        <option value="asc">Ascending</option>
                    </select>
                    <button
                        data-testid="sheets-refresh"
                        onClick={() => { setRefreshing(true); setReloadKey((k) => k + 1); }}
                        disabled={refreshing || loading}
                        className="inline-flex items-center gap-2 border border-white/10 text-[#C4C4C4] hover:text-[#D4AF37] hover:border-[#D4AF37]/40 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-all disabled:opacity-60"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
                        Refresh
                    </button>
                </div>

                {/* List */}
                {loading ? (
                    <div className="flex items-center justify-center py-32 text-[#888]">
                        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37] mr-3" />Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div data-testid="sheets-empty" className="border border-white/[0.06] bg-[#050505] py-24 text-center">
                        <Inbox className="w-10 h-10 text-[#D4AF37]/60 mx-auto mb-6" strokeWidth={1.25} />
                        <h3 className="font-display text-2xl text-white mb-2">No spreadsheets yet.</h3>
                        <p className="text-sm text-[#888] max-w-md mx-auto">
                            Upload an XLSX, XLS, or CSV to start building your library.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-px bg-[#D4AF37]/10" data-testid="sheets-list">
                        {items.map((r) => (
                            <article
                                key={r.id}
                                data-testid={`sheet-row-${r.id}`}
                                className="relative bg-black hover:bg-[#0A0A0A] transition-colors p-5 md:p-6"
                            >
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex items-start gap-4 flex-1 min-w-0 basis-full sm:basis-auto">
                                        <div className="w-10 h-10 shrink-0 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37]">
                                            <FileSpreadsheet className="w-5 h-5" strokeWidth={1.5} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-display text-lg text-white leading-tight truncate">{r.name}</h3>
                                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.22em] text-[#888]">
                                                <span className="text-[#D4AF37]">{r.ext}</span>
                                                <span>{fmtSize(r.size)}</span>
                                                <span>rev {r.revision}</span>
                                                <span>Modified {fmtDate(r.last_modified_at)}</span>
                                                <span className="hidden sm:inline">by {r.last_modified_by}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                                        <button
                                            data-testid={`sheet-download-${r.id}`}
                                            onClick={() => downloadOne(r)}
                                            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#C4C4C4] hover:text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]/50 px-3 py-1.5 transition-all"
                                        >
                                            <Download className="w-3 h-3" />Download
                                        </button>
                                        <button
                                            data-testid={`sheet-rename-${r.id}`}
                                            onClick={() => openRename(r)}
                                            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#C4C4C4] hover:text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]/50 px-3 py-1.5 transition-all"
                                        >
                                            <Pencil className="w-3 h-3" />Rename
                                        </button>
                                        <button
                                            data-testid={`sheet-duplicate-${r.id}`}
                                            onClick={() => duplicate(r)}
                                            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#C4C4C4] hover:text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]/50 px-3 py-1.5 transition-all"
                                        >
                                            <Copy className="w-3 h-3" />Duplicate
                                        </button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <button
                                                    data-testid={`sheet-delete-${r.id}`}
                                                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#888] hover:text-red-300 border border-transparent hover:border-red-500/40 px-3 py-1.5 transition-all"
                                                >
                                                    <Trash2 className="w-3 h-3" />Delete
                                                </button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent className="bg-[#0A0A0A] border border-white/[0.08] text-white">
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle className="font-display text-2xl">Delete this spreadsheet?</AlertDialogTitle>
                                                    <AlertDialogDescription className="text-[#C4C4C4] text-sm">
                                                        {r.name}. This soft-deletes the file — its underlying storage is retained but no longer visible in the library.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel className="bg-transparent border border-white/15 text-white hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => doDelete(r.id)} className="bg-red-600 text-white hover:bg-red-500">Delete</AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                <div className="mt-10 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[#666]">
                    <ClipboardList className="w-3.5 h-3.5 text-[#D4AF37]" />
                    Phase 1 · Library · Editor + version history arrive in the next phase
                </div>
            </div>

            {/* Rename dialog */}
            <AlertDialog open={!!renameTarget} onOpenChange={(v) => { if (!v) setRenameTarget(null); }}>
                <AlertDialogContent className="bg-[#0A0A0A] border border-white/[0.08] text-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-2xl">Rename spreadsheet</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#C4C4C4] text-sm">
                            The file extension is preserved automatically.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <input
                        data-testid="rename-input"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") submitRename(); }}
                        className="w-full bg-black border border-white/10 focus:border-[#D4AF37] px-4 py-3 text-sm text-white outline-none"
                        autoFocus
                    />
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border border-white/15 text-white hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="rename-submit" onClick={submitRename} className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">Save</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </main>
    );
}
